// What the site is set up with: its map and zones, languages and ops tablets. Before
// go-live it also says what's still needed.

import { useState } from 'react';
import type { SiteDraft } from '../../../../src/draft.ts';
import { api, message } from '../../api.ts';
import { ago, tabletOnline } from '../../format.ts';
import { byUsualOrder, englishName } from '../../languages.ts';
import { bboxAround, SiteMap, type BBox } from '../../map.tsx';
import type { SiteView } from '../../types.ts';

export function Setup({ site, reload }: { site: SiteView; reload: () => void }) {
  const draft = site.draft as SiteDraft;
  const shown = site.bundle ?? { zones: draft.zones, map: draft.map, messages: draft.messages, tablets: draft.tablets };
  const bbox = (shown.map?.bbox as BBox | undefined) ?? bboxAround(shown.zones);
  const checked = new Set(draft.checked ?? []);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      await api('POST', `/sites/${site.siteId}/send`, {});
      reload();
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  };

  const waiting = site.changes.some((c) => c.status === 'waiting');
  return (
    <div className="stack" style={{ gap: 20 }}>
      {!site.live && (
        <div className={site.readiness.blocking.length ? 'note amber' : 'note green'}>
          {site.readiness.blocking.length ? (
            <>
              <strong>Before it can be sent for approval:</strong>
              <ul style={{ margin: '6px 0 0', paddingLeft: 20 }}>
                {site.readiness.blocking.map((b) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
            </>
          ) : waiting ? (
            "Sent to the ops tablet. Nothing goes live until it's approved there."
          ) : (
            'Ready to send to the ops tablet for approval.'
          )}
        </div>
      )}
      {site.readiness.warnings.length > 0 && (
        <div className="note amber">
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {site.readiness.warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </div>
      )}
      <div className="split">
        <div className="stack">
          {bbox ? <SiteMap bbox={bbox} zones={shown.zones} alt={`Map of ${site.siteName} with its zones`} /> : <div className="card empty">No zones yet. They're drawn in the set-up steps.</div>}
          {!site.live && !waiting && (
            <div className="row">
              <button className="btn primary" disabled={busy || site.readiness.blocking.length > 0} onClick={send}>
                Send to the ops tablet for approval
              </button>
              {error && <span className="error">{error}</span>}
            </div>
          )}
        </div>
        <div className="grid2" style={{ alignContent: 'start' }}>
          <div className="card">
            <span className="card-label">Zones</span>
            {shown.zones.length === 0 && <span className="faint">None yet</span>}
            {shown.zones.map((z) => (
              <span key={z.id}>
                <span className="mono muted">{z.id}</span> {z.name}
              </span>
            ))}
          </div>
          <div className="card">
            <span className="card-label">Languages</span>
            {Object.keys(shown.messages ?? {}).sort(byUsualOrder).map((l) => (
              <span key={l}>
                {englishName(l)} {!checked.has(l) && <span className="amber">(not yet checked)</span>}
              </span>
            ))}
          </div>
          <div className="card">
            <span className="card-label">Ops tablet</span>
            {site.tablets.length === 0 && shown.tablets.length === 0 && <span className="faint">Not added yet</span>}
            {site.tablets.map((t) => (
              <span key={t.keyId} className="stack" style={{ gap: 2 }}>
                <span>{t.label}</span>
                <span className="muted small">
                  {[t.model, t.osVersion, t.appVersion && `Z.A.S. Ops ${t.appVersion}`].filter(Boolean).join(' · ')}
                </span>
                <span className={tabletOnline(t.lastSeenAt) ? 'green small' : 'faint small'}>{t.lastSeenAt ? `${tabletOnline(t.lastSeenAt) ? 'Online' : 'Last seen'} · ${ago(t.lastSeenAt)}` : 'Not seen yet'}</span>
              </span>
            ))}
          </div>
        </div>
      </div>
      {site.qr && (
        <p className="faint small">
          QR code for staff to join: <span className="mono">{site.qr}</span>
        </p>
      )}
    </div>
  );
}
